import React, { useEffect, useState } from "react";
import api from "../../api";
import { memberNavItems, staffNavItems, applyMenuOrder } from "../../config/navigation";
import "./MenuOrderEditor.css";

const MENUS = {
  member: { label: "Member menu", hint: "What members see after logging in.", items: memberNavItems },
  staff: { label: "Staff & leaders menu", hint: "Admins, secretaries, chairs and other staff roles.", items: staffNavItems },
};

const paths = (items) => items.map((item) => item.path);

// Lets administrators reorder the sidebar (and mobile bottom nav) by dragging or with ▲ ▼.
export default function MenuOrderEditor({ onMessage }) {
  const [menu, setMenu] = useState("member");
  const [order, setOrder] = useState({ member: paths(memberNavItems), staff: paths(staffNavItems) });
  const [saved, setSaved] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.getMenuOrder()
      .then((response) => {
        const data = response.data || {};
        const next = {
          member: paths(applyMenuOrder(memberNavItems, data.member)),
          staff: paths(applyMenuOrder(staffNavItems, data.staff)),
        };
        setOrder(next);
        setSaved(next);
      })
      .catch((error) => onMessage({ type: "error", text: error.message }));
  }, [onMessage]);

  const byPath = Object.fromEntries(MENUS[menu].items.map((item) => [item.path, item]));
  const list = order[menu].filter((path) => byPath[path]);
  const changed = saved && JSON.stringify(saved[menu]) !== JSON.stringify(order[menu]);

  const move = (from, to) => {
    if (to < 0 || to >= list.length || from === to) return;
    const next = [...list];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    setOrder((current) => ({ ...current, [menu]: next }));
  };

  const save = async () => {
    setSaving(true);
    onMessage({ type: "", text: "" });
    try {
      const response = await api.saveMenuOrder(order);
      setSaved(order);
      window.dispatchEvent(new CustomEvent("tarewu:menu-order-updated", { detail: response.data }));
      onMessage({ type: "success", text: response.message });
    } catch (error) {
      onMessage({ type: "error", text: error.message });
    } finally {
      setSaving(false);
    }
  };

  const reset = () => setOrder((current) => ({ ...current, [menu]: paths(MENUS[menu].items) }));

  return (
    <section className="settings-card menu-order-card">
      <div className="settings-card-title">
        <div>
          <h2>Menu order</h2>
          <p>Drag items, or use ▲ ▼, to arrange the sidebar from top to bottom. On phones the first four items appear in the bottom bar.</p>
        </div>
        <span className={changed ? "warning" : "ready"}>{changed ? "Unsaved changes" : "Saved"}</span>
      </div>

      <div className="menu-order-switch" role="tablist">
        {Object.entries(MENUS).map(([key, value]) => (
          <button key={key} type="button" role="tab" aria-selected={menu === key} className={menu === key ? "active" : ""} onClick={() => setMenu(key)}>
            <b>{value.label}</b>
            <small>{value.hint}</small>
          </button>
        ))}
      </div>

      <ol className="menu-order-list">
        {list.map((path, index) => {
          const item = byPath[path];
          return (
            <li
              key={path}
              draggable
              className={`${dragIndex === index ? "dragging" : ""} ${index < 4 ? "in-bottom-bar" : ""}`}
              onDragStart={() => setDragIndex(index)}
              onDragOver={(event) => { event.preventDefault(); if (dragIndex !== null && dragIndex !== index) { move(dragIndex, index); setDragIndex(index); } }}
              onDragEnd={() => setDragIndex(null)}
            >
              <span className="menu-order-handle" aria-hidden="true">⋮⋮</span>
              <span className="menu-order-position">{index + 1}</span>
              <span className="menu-order-icon" aria-hidden="true">{item.icon}</span>
              <span className="menu-order-label">
                <b>{item.label}</b>
                <small>{path}{index < 4 ? " · mobile bottom bar" : ""}</small>
              </span>
              <span className="menu-order-buttons">
                <button type="button" aria-label={`Move ${item.label} up`} disabled={index === 0} onClick={() => move(index, index - 1)}>▲</button>
                <button type="button" aria-label={`Move ${item.label} down`} disabled={index === list.length - 1} onClick={() => move(index, index + 1)}>▼</button>
              </span>
            </li>
          );
        })}
      </ol>

      <div className="settings-save-bar">
        <span>Menus a user is not allowed to see stay hidden; section headings are hidden while a custom order is used.</span>
        <div className="menu-order-actions">
          <button type="button" className="menu-order-reset" onClick={reset} disabled={saving}>Reset to default</button>
          <button type="button" onClick={save} disabled={saving || !changed}>{saving ? "Saving…" : "Save menu order"}</button>
        </div>
      </div>
    </section>
  );
}
